import { useEffect, useState } from "react";
import { useAuthHeader, useAuthUser } from "../../../../Services/Auth/sessionAuth";
import { Button, Form, Space, Spin, Tabs, Tag, Tooltip } from 'antd';
import Utilisateur from "../../../../Models/Utilisateur";
import type { TabsProps } from 'antd';
import { UserOutlined, EyeOutlined } from '@ant-design/icons';
import SweetAlert from "sweetalert2";
import { Crypter } from "../../../../Functions/Security/Crypter";
import Data from '../../../../Data/Data.json';
import axios from "axios";
import Tab1 from "./Tab1";
import Tab2 from "./Tab2";
import { Typography, IconButton, Stack, Grid, Box } from "@mui/material";
import { Edit, Refresh } from '@mui/icons-material';
import { normalizeKeysToUppercase } from "../../../../utilitaires/globalFunction";
import Params from "../../../../Types/ParamsPicklist";
import dayjs from 'dayjs';
import AssetDocumentPreviewModal from '../../../AssetDocumentPreviewModal';
import { ActifDocumentType, extractActifDocumentRows, getActifDocumentLabel, loadActifDocumentTypes, mapActifDocumentsToPreview, toDocumentDataUri } from '../../../../Services/actifDocumentTypes';

const buildDocuments = (row: any, definitions: ActifDocumentType[]) => {
    // Si on a des documents réels, les utiliser
    const documents = row.Documents || row.DOCUMENTS || row.documents || [];
    if (documents.length > 0) {
        return documents.map((document: any) => ({
            label: getActifDocumentLabel(document, definitions),
            base64: toDocumentDataUri(document)
        })).filter((document: any) => document.base64);
    }
    
    // Sinon, créer des entrées factices basées sur NbDocuments pour l'affichage du compteur
    // NbDocuments représente le nombre total de fichiers uploadés
    const nbDocuments = row.NBDOCUMENTS || row.NbDocuments || 0;
    if (nbDocuments > 0) {
        return Array.from({ length: nbDocuments }, (_, index) => ({
            label: `Document ${index + 1}`,
            base64: null // Pas de contenu pour les documents factices
        }));
    }
    
    return [];
};

const DemandeActif = () => {
    const auth = useAuthUser();
    const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [Consultactv, setConsulte] = useState(false);
    const [Annulactv, setAnnule] = useState(true);
    const [FormAct, setFormactve] = useState(true);
    const [categoryConsulted, setCategoryConsulted] = useState(false);
    const [codeCategorieConsultee, setCodeCategorieConsultee] = useState<string>("");

    const [activeTabKey, setActiveTabKey] = useState('1');
    const isOnTab2 = activeTabKey === '2';

    const [glActifs, setglActifs] = useState<any[]>([]);
    const [glActifsValides, setglActifsValides] = useState<any[]>([]);
    const [listFinancements, setListFinancements] = useState<any[]>([]);
    const [documentDefinitions, setDocumentDefinitions] = useState<ActifDocumentType[]>([]);

    const [docModal, setDocModal] = useState<{ open: boolean; docs: { label: string; base64: string | null }[]; activeIndex: number | null }>({
        open: false, docs: [], activeIndex: null
    });

    // Fonction pour charger les documents d'un actif et ouvrir la modal
    const openDocsModalWithLoad = async (row: any) => {
        // Si les documents sont déjà chargés avec leur contenu, les utiliser directement
        const rowDocuments = row.Documents || row.DOCUMENTS || row.documents || [];
        if (rowDocuments.length > 0) {
            const docs = buildDocuments(row, documentDefinitions);
            if (docs.length > 0) {
                setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
                return;
            }
        }
        // Charger les documents depuis l'API (GetActifWithDocuments si dispo, sinon GetActifsByValidation)
        try {
            setLoading(true);
            const getActifWithDocuments = (Data.ApiUrl as any).GetActifWithDocuments;
            const useNewEndpoint = !!getActifWithDocuments;
            const headers: any = {
                'Authorization': authHeader(),
                'CypherCodeActif': Crypter.encrypt(row.CODEACTIF)
            };
            if (!useNewEndpoint) {
                headers['CypherStatutValidation'] = Crypter.encrypt(row.STATUTVALIDATION || 'EN_ATTENTE');
            }
            const response = await axios.get(useNewEndpoint ? getActifWithDocuments : Data.ApiUrl.GetActifsByValidation, { headers });

            const res = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (res.StatutCode === 200) {
                const docRows = extractActifDocumentRows(res.Result, row.CODEACTIF);
                const docs = mapActifDocumentsToPreview(docRows, documentDefinitions);
                setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
            } else {
                setDocModal({ open: true, docs: [], activeIndex: null });
            }
        } catch (error) {
            console.error('Erreur chargement documents:', error);
            setDocModal({ open: true, docs: [], activeIndex: null });
        } finally {
            setLoading(false);
        }
    };

    const openDocsModal = (docs: { label: string; base64: string | null }[]) => setDocModal({ open: true, docs, activeIndex: docs.length === 1 ? 0 : null });
    const closeDocsModal = () => setDocModal({ open: false, docs: [], activeIndex: null });

    const loadDynamicList = (params: Params, setter: (data: any[]) => void) => {
        const config = {
            method: 'get', url: Data.ApiUrl.AnySearch,
            headers: { 'Content-Type': 'application/json', 'Authorization': authHeader(), 'CypherSearch': Crypter.encrypt(JSON.stringify(params)) }
        };
        axios(config).then((res) => {
            const response = JSON.parse(Crypter.decrypt(res.data)).Value;
            if (response.StatutCode === 200) {
                const table = response.Result.Table || response.Result || [];
                setter(table.map((item: any) => ({ CODE: item[params.Colonne1], LIBELLE: item[params.Colonne2] })));
            }
        }).catch(err => console.error("Erreur chargement liste:", err));
    };

    useEffect(() => {
        loadDynamicList({ Table: "TypeFinancement", Colonne1: "CodeFinancement", Colonne2: "Libelle", Choix: 22 }, setListFinancements);
        loadActifDocumentTypes(authHeader()).then(setDocumentDefinitions).catch(() => setDocumentDefinitions([]));
    }, []);

    // --- FIX : remplace la lecture du tableau imbriqué "Actifs" (incomplet et filtré
    // sur le mauvais champ STATUT) par le même mécanisme que CategorieActif.tsx :
    // un appel dédié à GetActifsByValidation par statut, filtré côté client par
    // CODECATEGORIE. Corrige à la fois les actifs rejetés invisibles ET les champs
    // manquants (Fournisseur, Grade, Localisation) qui n'étaient pas fiables dans
    // le tableau imbriqué.
    const fetchActifsByStatut = (statut: 'EN_ATTENTE' | 'REJETE' | 'VALIDE', catCode: string): Promise<any[]> => {
        // Ne pas envoyer CypherCodeActif quand on veut juste filtrer par statut
        const headers: any = { 
            'Authorization': authHeader(), 
            'CypherStatutValidation': Crypter.encrypt(statut)
        };
        
        return axios.get(Data.ApiUrl.GetActifsByValidation, { headers })
        .then((response) => {
            const res = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (res.StatutCode !== 200) return [];
            const rawData = res.Result?.Table || res.Result || [];
            const normalized = normalizeKeysToUppercase(Array.isArray(rawData) ? rawData : [rawData]);
            return normalized.filter((a: any) => a.CODECATEGORIE === catCode);
        }).catch(() => []);
    };

    function HandleConsult() {
        const code = form.getFieldValue("CODECATEGORIE");
        if (!code) {
            SweetAlert.fire("Alerte", "Veuillez choisir un code catégorie.", "warning");
            return;
        }

        setLoading(true);
        axios.get(Data.ApiUrl.GetByCodeCategorie, {
            headers: { 'Authorization': authHeader(), 'CypherCodeCategorie': Crypter.encrypt(code) }
        }).then((response) => {
            const res = JSON.parse(Crypter.decrypt(response.data)).Value;

            if (res.StatutCode === 200) {
                const d = normalizeKeysToUppercase(res.Result);

                form.setFieldsValue({
                    CODECATEGORIE: d.CODECATEGORIE,
                    LIBELLE: d.LIBELLE,
                    FAMILLE: d.CODEFAMILLE,
                    lib_FAMILLE: d.LIBELLEFAMILLE,
                    FINANCEMENT_ELIGIBLES: d.FINANCEMENTS,
                    REGLESUSAGE: d.REGLESUSAGE,
                    ESTLICITE: d.ESTLICITE,
                    ESTACTIF: d.ESTACTIF
                });

                const catCode = d.CODECATEGORIE;

                // FIX : trois appels dédiés (EN_ATTENTE, REJETE, VALIDE) au lieu de
                // filtrer le tableau imbriqué "Actifs" sur le mauvais champ STATUT.
                Promise.all([
                    fetchActifsByStatut('EN_ATTENTE', catCode),
                    fetchActifsByStatut('REJETE', catCode),
                    fetchActifsByStatut('VALIDE', catCode)
                ]).then(([enAttente, rejetes, valides]) => {
                    const combined = [...enAttente, ...rejetes].map((a: any, idx: number) => ({
                        ...a,
                        _key: a.CODEACTIF || `line_${idx}_${Date.now()}`
                    }));
                    setglActifs(combined);
                    setglActifsValides(valides);
                    setLoading(false);
                });

                setCodeCategorieConsultee(catCode);
                setCategoryConsulted(true);
                setAnnule(false);
            } else {
                setLoading(false);
                SweetAlert.fire({ title: res.Message, text: res.Result, icon: "warning" });
            }
        }).catch((err) => {
            setLoading(false);
            SweetAlert.fire("Erreur", "Détail technique : " + err.message, "error");
        });
    }

    function HandleAnnul() {
        setConsulte(false);
        setAnnule(true);
        setCategoryConsulted(false);
        setCodeCategorieConsultee("");
        form.resetFields();
        setglActifs([]);
        setglActifsValides([]);
    }

    // FIX : ancienne version inversée (`if (codeCategorieConsultee) return;`)
    // qui empêchait tout rafraîchissement. Simplifié : relance simplement
    // HandleConsult(), qui relit le champ CODECATEGORIE déjà rempli dans le form.
    const handleRefresh = () => {
        if (!codeCategorieConsultee) return;
        HandleConsult();
    };

    // --- SAUVEGARDE DIRECTE (création ou modification) EN BASE ---
    const handleSaveSingleActif = (actifData: any) => {
        setLoading(true);

        const isUpdate = !!actifData.CODEACTIF;
        const url = isUpdate ? Data.ApiUrl.UpdateActif : Data.ApiUrl.CreateActifBatch;
        // Alt si UpdateActif n'existe pas côté backend : Data.ApiUrl.CreateActifBatch
        // en PUT, ou un endpoint dédié Actif/UpdateActif à créer.

        const itemPayload = {
            CodeActif: actifData.CODEACTIF,
            IdentifiantUnique: actifData.IDENTIFIANTUNIQUE?.trim() ? actifData.IDENTIFIANTUNIQUE : null,
            CodeCategorie: codeCategorieConsultee,
            Designation: actifData.DESIGNATION,
            // FIX : lisait actifData.CODEFOURNISSEUR (toujours vide) au lieu de
            // actifData.FOURNISSEUR (le champ réellement rempli par la modale).
            // C'est ce bug qui envoyait systématiquement CodeFournisseur=null.
            CodeFournisseur: actifData.FOURNISSEUR?.trim() ? actifData.FOURNISSEUR : null,
            CoutInitial: actifData.COUTACHAT || 0,
            // FIX : "actifData.GRADEDESCRIPTION || actifData.GRADEDESCRIPTION" ne
            // faisait rien d'utile ; GRADE est le vrai code (A-E) choisi dans la modale.
            Grade: actifData.GRADE || "A",
            Statut: actifData.STATUT || "EN_ACQUISITION",
            // FIX : lisait actifData.LOCALISATIONCOURANTE (champ inexistant côté
            // modale) au lieu de actifData.LOCALISATION. Même bug que Fournisseur.
            LocalisationCourante: actifData.LOCALISATION?.trim() ? actifData.LOCALISATION : null,
            ValeurResiduelle: actifData.VALEURRESIDUELLE || 0,
            StatutValidation: "EN_ATTENTE",
            CommentaireValidation: null,
            EnregistrePar: Perss.coduti,
            // FIX: Utiliser DocumentsToUpload pour l'upload (nouveau nom backend)
            // Les documents déjà persistés (NumDocument != null) ne sont pas
            // ré-uploadés ; seuls les nouveaux fichiers partent dans DocumentsToUpload.
            DocumentsToUpload: (actifData.DOCUMENTS || [])
                .filter((document: any) => document.NumDocument == null)
                .map((document: any) => ({
                    TypeCodeDoc: document.TypeCodeDoc,
                    Valeur: document.Valeur
                })),
            // Documents persistés supprimés dans la modale (leurs NumDocument)
            DocumentsToDelete: actifData.DocumentsToDelete || [],
            TypesDocuments: (actifData.DOCUMENTS || []).map((document: any) => document.TypeCodeDoc)
        };

        const payload = isUpdate ? itemPayload : { CodeCategorie: codeCategorieConsultee, Actifs: [itemPayload] };

        const encryptedData = Crypter.encrypt(JSON.stringify(payload));

        axios({
            method: isUpdate ? 'put' : 'post',
            url: url,
            data: JSON.stringify(encryptedData),
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' }
        }).then((response) => {
            const resJson = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (resJson.StatutCode === 200) {
                SweetAlert.fire("Succès", resJson.Message || "L'actif a été enregistré.", "success");
                handleRefresh();
            } else {
                setLoading(false);
                SweetAlert.fire({ title: resJson.Message || "Erreur de validation", text: resJson.Result, icon: "warning" });
            }
        }).catch((err) => {
            setLoading(false);
            console.error("Erreur Direct Save:", err);
            SweetAlert.fire("Erreur", "Le serveur n'a pas pu traiter l'enregistrement.", "error");
        });
    };

    const formatMontant = (val: number) => new Intl.NumberFormat('fr-FR').format(val || 0);
    const getStatutColor = (statut: string) => statut === 'REJETE' ? 'red' : 'default';

    const columns = [
        {
            key: 'CODEACTIF', label: 'Code Actif', width: 100, align: 'center' as const,
            render: (row: any) => <span style={{ fontWeight: 'bold' }}>{row?.CODEACTIF || '-'}</span>
        },
        {
            key: 'IDENTIFIANTUNIQUE', label: 'N° Série / Unique', width: 200,
            render: (row: any) => <span style={{ fontSize: '0.85rem' }}>{row?.IDENTIFIANTUNIQUE}</span>
        },
        {
            key: 'DESIGNATION', label: 'Désignation', width: 200,
            render: (row: any) => <div className="whitespace-normal break-words">{row?.DESIGNATION}</div>
        },
        {
            // FIX : le champ réel renvoyé par le backend est COUTINITIAL (comme
            // confirmé par CategorieActif.tsx), pas COUTACHAT.
            key: 'COUTINITIAL', label: "Coût d'achat(XAF)", width: 150, align: 'right' as const,
            render: (row: any) => <span style={{ fontWeight: 'bold' }}>{formatMontant(row?.COUTINITIAL)}</span>
        },
        {
            key: 'VALEURRESIDUELLE', label: "V. Résiduelle", width: 120, align: 'right' as const,
            render: (row: any) => <span style={{ color: '#666' }}>{formatMontant(row?.VALEURRESIDUELLE)}</span>
        },
        {
            key: 'GRADE', label: 'Grade', width: 80, align: 'center' as const,
            render: (row: any) => <Tag color="blue">{row?.GRADEDESCRIPTION || row?.CODEGRADE}</Tag>
        },
        {
            // FIX : lisait row.FOURNISSEUR (inexistant) au lieu de CODEFOURNISSEUR,
            // avec tooltip sur LIBELLEFOURNISSEUR (nom réel du libellé).
            key: 'CODEFOURNISSEUR', label: 'Fournisseur', width: 110, align: 'center' as const,
            render: (row: any) => (
                <Tooltip title={row?.LIBELLEFOURNISSEUR}>
                    <span>{row?.CODEFOURNISSEUR}</span>
                </Tooltip>
            )
        },
        {
            // FIX : le champ réel est LOCALISATIONCOURANTE, pas LOCALISATION.
            key: 'LOCALISATIONCOURANTE', label: 'Localisation', width: 150,
            render: (row: any) => <span>{row?.LOCALISATIONCOURANTE}</span>
        },
        {
            key: 'STATUT', label: 'État Opér.', width: 110,
            render: (row: any) => <Tag color="cyan">{row?.STATUT?.replace('_', ' ')}</Tag>
        },
        {
            key: 'STATUTVALIDATION', label: 'Statut', width: 110,
            render: (row: any) => <Tag color={getStatutColor(row?.STATUTVALIDATION || 'EN_ATTENTE')}>{row?.STATUTVALIDATION || 'EN ATTENTE'}</Tag>
        },
        {
            key: 'MOTIF_REJET', label: 'Motif Rejet', width: 200,
            render: (row: any) => <span style={{ color: 'red', fontSize: '0.75rem' }}>{row?.COMMENTAIREVALIDATION}</span>
        },
        {
            // FIX : le champ réel est ENREGISTREPAR (DTO EnregistrePar), pas SAISIPAR.
            key: 'ENREGISTREPAR', label: 'Saisie par', width: 130,
            render: (row: any) => (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <b style={{ fontSize: '0.8rem' }}>{row?.ENREGISTREPAR}</b>
                    <span style={{ fontSize: '0.65rem', color: 'gray' }}>
                        {row?.DATEENREGISTREMENT ? dayjs(row.DATEENREGISTREMENT).format('DD/MM/YYYY HH:mm') : '-'}
                    </span>
                </Box>
            )
        },
        {
            key: 'DOCUMENTS', label: 'Documents', width: 100, align: 'center' as const,
            render: (row: any) => {
                const count = row.NBDOCUMENTS ?? row.NbDocuments ?? 0;
                return (
                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <IconButton size="small" onClick={() => openDocsModalWithLoad(row)} disabled={count === 0 && !row.CODEACTIF}>
                            <EyeOutlined style={{ fontSize: 18, color: count > 0 ? '#2A6596' : '#ccc' }} />
                        </IconButton>
                        <Tag color={count === 0 ? 'default' : 'green'}>{count}</Tag>
                    </Stack>
                );
            }
        },
        {
            key: 'ACTIONS', label: 'Actions', width: 90, align: 'center' as const,
            render: (row: any, _index: number, actions: { onEdit: (key: string) => void; }) => (
                <Stack direction="row" spacing={0.5} justifyContent="center">
                    <IconButton size="small" color="primary" onClick={() => actions.onEdit(row._key)}>
                        <Edit sx={{ fontSize: 18 }} />
                    </IconButton>
                </Stack>
            )
        }
    ];

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: <span><UserOutlined /> Création d'Actifs</span>,
            children: <Tab1
                form={form}
                glActifs={glActifs}
                setglActifs={setglActifs}
                disabledForm={FormAct}
                categoryConsulted={categoryConsulted}
                listFinancements={listFinancements}
                columns={columns}
                onSaveDirect={handleSaveSingleActif}
                codeCategorieConsultee={codeCategorieConsultee} />
        },
        {
            key: '2',
            label: <span><UserOutlined /> Inventaire Validé</span>,
            children: <Tab2 glActifsValides={glActifsValides} form={form} disabledForm={FormAct} listFinancements={listFinancements} />
        }
    ];

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'><b> DEMANDE D'ACTIFS </b></Typography>
            <Spin spinning={loading} size='large' style={{ color: 'green' }}>
                <Form form={form} preserve labelAlign="left">
                    <div className='bG'>
                        <legend><Typography color={"darkgreen"}>Actions</Typography></legend>
                        <Form.Item className='bouttong'>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button size='large' disabled={Consultactv || isOnTab2} onClick={HandleConsult} block>Consulter</Button>
                                <Button size='large' disabled={Annulactv || isOnTab2} onClick={HandleAnnul} block>Annuler</Button>
                                <Button size='large' disabled={!isOnTab2 || !codeCategorieConsultee} onClick={handleRefresh} block>Actualiser</Button>
                            </Space>
                        </Form.Item>
                    </div>
                    <div className='clientP'>
                        <Tabs
                            size='large'
                            type="card"
                            activeKey={activeTabKey}
                            onChange={setActiveTabKey}
                            items={items}
                        />
                    </div>
                </Form>
            </Spin>

            <AssetDocumentPreviewModal
                open={docModal.open}
                docs={docModal.docs}
                activeIndex={docModal.activeIndex}
                onSelect={(index) => setDocModal(prev => ({ ...prev, activeIndex: index }))}
                onBack={() => setDocModal(prev => ({ ...prev, activeIndex: null }))}
                onClose={closeDocsModal}
            />
        </div>
    );
};

export default DemandeActif;